import { Directive, HostBinding, Input } from '@angular/core'
import { AutomationDestroyableSaver } from '@tv3/containers/automations/automation-destroyable-saver'
import {
  AutomationEventTypes,
  AutomationFormData,
  AutomationHandler,
  AutomationHelpers,
  AutomationResponse,
  AutomationServiceTypes,
  AutomationTypes,
  AutomationViewGroup,
  DeleteAutomation,
  PauseAutomation,
  selectAutomationGroupsByType,
  selectIsAddingAutomation,
  selectIsUpdatingAutomation,
  UnPauseAutomation,
} from '@tokeet-frontend/automations'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { select, Store } from '@ngrx/store'
import { filter, map, mapTo, pairwise, switchMap, take } from 'rxjs/operators'
import * as lodash from 'lodash'
import {
  ConfirmDialogStatus,
  distinctObjectUntilChangedByFields,
  isSomething,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { ConfirmDialogService } from '@tokeet-frontend/tv3-platform'

export interface AutopilotAutomationConfigurableData
  extends Pick<
    AutomationFormData,
    'users' | 'status' | 'guest' | 'template' | 'templates' | 'data' | 'delayed' | 'categories'
  > {
  hours?: number
  days?: number
}

@Directive()
export abstract class AutopilotAutomationBaseComponent extends AutomationDestroyableSaver {
  @Input() label: string
  @Input() name: string
  //
  @Input() service = AutomationServiceTypes.Advance

  isTimeBasedEvent = false
  _event: AutomationEventTypes
  @Input() set event(event: AutomationEventTypes) {
    this.isTimeBasedEvent = AutomationHelpers.isTimeEvent(event)
    this._event = event
  }
  get event() {
    return this._event
  }
  @Input() isTimeEditable = false

  @Input() type: AutomationTypes // Automation type
  @Input() handler: AutomationHandler
  @Input() eventSourceType: AutomationTypes // Event Source type

  // automation settings
  @Input() filterByCategories = false
  @Input() categories: { value: string; label: string }[] = [] // expense, extra, incident...

  @Input() delayed = false
  @Input() delayedMax = 36
  @Input() days = 0
  @Input() hours = 0
  @Input() rentals: string[] = []
  @Input() channels: string[] = []
  @Input() lateBookings = false

  @Input() deletable = false
  @Input() idSuffix: string
  @Input() customTooltip: string
  @Input() disabled = false

  @HostBinding('class') get cssClass() {
    return this.viewType === 'table' ? 'autopilot-automation table-view' : 'autopilot-automation list-view'
  }

  @Input() viewType: 'table' | 'list' = 'list'

  isInputAutomation = false

  @Input() set automation(automation: AutomationViewGroup) {
    this.automationGroup = automation

    if (automation) {
      this.isInputAutomation = true
      if (this.automation$ instanceof BehaviorSubject) {
        this.automation$.next(automation)
      }
    }
  }

  form = new UntypedFormGroup({
    days: new UntypedFormControl(0, [Validators.required]),
    hours: new UntypedFormControl(5, [Validators.required]),
    status: new UntypedFormControl(false),
  })

  isEditing$: Observable<boolean>

  // all automation groups
  automationGroups: AutomationViewGroup[] = []
  automations$: Observable<AutomationViewGroup[]>

  // first automation group
  automationGroup: AutomationViewGroup
  automation$: Observable<AutomationViewGroup> = new BehaviorSubject<AutomationViewGroup>(null)

  protected constructor(
    protected store: Store<any>,
    protected amplitudeService: AmplitudeService,
    protected confirm: ConfirmDialogService
  ) {
    super(store, amplitudeService)
  }

  abstract getAutopilotId(): string

  ngOnInit(): void {
    if (this.delayed) {
      this.form.addControl(
        'delayed',
        new UntypedFormControl(24, [
          Validators.min(0),
          this.delayedMax > 0 ? Validators.max(this.delayedMax) : Validators.max(72),
        ])
      )
    }

    if (this.lateBookings) {
      this.form.addControl('late_bookings', new UntypedFormControl(false))
    }

    if (this.filterByCategories) {
      this.form.addControl('categories', new UntypedFormControl([]))
    }

    this.form
      .get('status')
      .valueChanges.pipe(
        switchMap((value) =>
          this.confirm
            .openRegular({
              title: value ? $localize`Enable this Automation?` : $localize`Disable this Automation?`,
              body: value
                ? $localize`Do you want to enable this automation?`
                : $localize`Do you want to disable this automation?`,
            })
            .pipe(
              filter((status) => {
                if (status === ConfirmDialogStatus.Confirmed) {
                  return true
                }
                this.form.get('status').setValue(!value, { emitEvent: false })
                return false
              }),
              mapTo(value)
            )
        ),
        untilDestroy(this)
      )
      .subscribe((value) => {
        this.tryToCreateOrUpdateAutomation(value)
      })

    this.automations$ = this.store.pipe(
      select(selectAutomationGroupsByType(this.type, false)),
      filter(() => !this.isInputAutomation),
      map((items) => items.filter((item) => this.filterAutomation(item)))
    )
    this.automations$.pipe(untilDestroy(this)).subscribe((items) => {
      this.automationGroups = items
    })

    if (!this.isInputAutomation) {
      this.automation$ = this.automations$.pipe(map((items) => lodash.head(items)))
      this.automation$.pipe(untilDestroy(this)).subscribe((item) => {
        this.automationGroup = item
      })
    }

    this.automation$
      .pipe(
        filter(isSomething),
        distinctObjectUntilChangedByFields(['status', 'days', 'hours', 'categories', 'delayed', 'late_bookings']),
        untilDestroy(this)
      )
      .subscribe((item) => {
        this.form?.patchValue(
          {
            status: !!item?.status,
            days: item?.settings?.days || 0,
            hours: item?.settings?.hours || 0,
            categories: item?.settings?.categories || [],
            delayed: item?.attributes?.delayed,
            late_bookings: !!item?.settings?.late_bookings,
          },
          { emitEvent: false }
        )
      })

    this.automation$.pipe(pairwise(), untilDestroy(this)).subscribe(([prev, current]) => {
      if (prev && !current) {
        this.form?.patchValue({ status: false }, { emitEvent: false })
      }
    })

    this.isEditing$ = combineLatest([
      this.store.pipe(select(selectIsAddingAutomation)),
      this.store.pipe(select(selectIsUpdatingAutomation)),
    ]).pipe(
      map(([adding, updating]) => adding || updating),
      untilDestroy(this)
    )

    // fix: remove duplicated items
    this.automation$.pipe(filter(isSomething), take(1)).subscribe((group) => {
      this.fixDuplicateAutomations(group?.items || [])
    })
  }

  filterAutomation(item: AutomationResponse | AutomationViewGroup) {
    if (item.attributes?.autopilot !== this.getAutopilotId()) return false
    if (item.service !== this.service) return false
    if (item.handler !== this.handler) return false
    if (item.event === AutomationEventTypes.TimeBasedEvent) {
      return item.timeevent === this.event
    } else {
      return item.event === this.event
    }
  }

  private fixDuplicateAutomations(items: AutomationResponse[]) {
    if (items?.length < 2) return
    const groups = lodash.groupBy(items, (t) => (t.settings?.rental || '') + (t.settings?.channel || ''))
    const duplicatesToDelete: AutomationResponse[] = lodash.compact(
      lodash.flatten(
        lodash.map(groups, (data) => {
          if (data?.length < 2) return []
          return data.slice(1)
        })
      )
    )

    const keys = lodash.keys(groups)
    // empty key should not exist with other keys
    if (keys.length > 1 && isSomething(groups[''])) {
      duplicatesToDelete.push(...groups[''])
    }

    if (isSomething(duplicatesToDelete)) {
      this.store.dispatch(DeleteAutomation({ ids: duplicatesToDelete.map((t) => t.pkey), silent: true }))
    }
  }

  toggleAutomation(status: boolean) {
    if (!this.automationGroup) return

    this.store.dispatch(
      status ? UnPauseAutomation({ ids: this.automationGroup.ids }) : PauseAutomation({ ids: this.automationGroup.ids })
    )
  }

  abstract tryToCreateOrUpdateAutomation(status: boolean): void

  protected saveAutomation(formData: AutopilotAutomationConfigurableData) {
    const timeevent = !this.isTimeBasedEvent
      ? undefined
      : {
          days: (this.isTimeEditable ? formData.days : this.days) || 0,
          hours: (this.isTimeEditable ? formData.hours : this.hours) || 0,
          event: this.event,
        }

    this.save(
      {
        ...formData,
        service: this.service,
        rentals: this.rentals || [],
        channels: this.channels || [],
        name: this.name || this.label,
        handler: this.handler,
        event: this.isTimeBasedEvent ? AutomationEventTypes.TimeBasedEvent : this.event,
        timeevent,
        type: this.type,
        attributes: {
          autopilot: this.getAutopilotId(),
        },
      },
      this.automationGroup,
      true
    )
  }

  getTooltip(notifyGuest?: boolean) {
    const handler = this.automationGroup?.handler || this.handler
    const status = !!this.automationGroup?.status
    const hasGuest = lodash.isBoolean(notifyGuest)
      ? notifyGuest
      : AutomationHelpers.sendToGuest(this.automationGroup?.items || [])

    switch (handler) {
      // Message
      case AutomationHandler.SendEmailMessage:
      case AutomationHandler.SendSMSMessage:
      case AutomationHandler.SendPushNotification:
        if (hasGuest) {
          return status
            ? $localize`Turn the toggle off if you don't want to notify guests`
            : $localize`Turn the toggle on if you want to notify guests`
        } else {
          return status
            ? $localize`Turn the toggle off if you don't want to notify users`
            : $localize`Turn the toggle on if you want to notify users`
        }
      case AutomationHandler.ExpenseNotifyGuestHandler:
      case AutomationHandler.DeviceNotifyGuestHandler:
      case AutomationHandler.InvoiceSendToGuestHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to notify guests`
          : $localize`Turn the toggle on if you want to notify guests`
      case AutomationHandler.TaskNotifyCreator:
      case AutomationHandler.TaskNotifyAssignee:
      case AutomationHandler.TaskNotifyOldAssignee:
        return status
          ? $localize`Turn the toggle off if you don't want to notify users`
          : $localize`Turn the toggle on if you want to notify users`
      // Booking
      case AutomationHandler.BookingMarkFollowUp:
      case AutomationHandler.BookingConfirmHandler:
      case AutomationHandler.BookingMarkPaid:
      case AutomationHandler.BookingMarkUnpaid:
        return status
          ? $localize`Turn the toggle off if you don't want to update booking status`
          : $localize`Turn the toggle on if you want to update booking status`
      // Task
      case AutomationHandler.TaskCreateHandler:
      case AutomationHandler.IncidentCreateTaskHandler:
      case AutomationHandler.BookingCreateTaskHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to create task`
          : $localize`Turn the toggle on if you want to create task`
      case AutomationHandler.BookingRemoveTaskHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to remove all existing tasks for this booking`
          : $localize`Turn the toggle on if you want to remove all existing tasks for this booking`
      // Expense
      case AutomationHandler.ExpenseCreateHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to create expense`
          : $localize`Turn the toggle on if you want to create expense`
      case AutomationHandler.ExpenseMarkPaidHandler:
      case AutomationHandler.ExpenseMarkUnpaidHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to update expense status`
          : $localize`Turn the toggle on if you want to update expense status`
      // Rental
      case AutomationHandler.RentalMarkReadyHandler:
      case AutomationHandler.RentalMarkNotReadyHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to update rental status`
          : $localize`Turn the toggle on if you want to update rental status`
      // Incident
      case AutomationHandler.IncidentCreateHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to create incident`
          : $localize`Turn the toggle on if you want to create incident`

      case AutomationHandler.TaskMarkIncidentResolved:
        return status
          ? $localize`Turn the toggle off if you don't want to update incident status`
          : $localize`Turn the toggle on if you want to update incident status`
      // Invoice
      case AutomationHandler.InvoiceMarkPaidHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to update invoice status`
          : $localize`Turn the toggle on if you want to update invoice status`
      case AutomationHandler.InvoiceChargeHandler:
        return status
          ? $localize`Turn the toggle off if you don't want to charge invoice`
          : $localize`Turn the toggle on if you want to charge invoice`

      // Review
      case AutomationHandler.SendReview:
        return status
          ? $localize`Turn the toggle off if you don't want to review the guest`
          : $localize`Turn the toggle on if you want to review the guest`
      case AutomationHandler.ReviewRespond:
        return status
          ? $localize`Turn the toggle off if you don't want to respond to guest review`
          : $localize`Turn the toggle on if you want to respond to guest review`
      case AutomationHandler.ReviewDraftAIReview:
        return status
          ? $localize`Turn the toggle off if you don't want to generate AI based drafts`
          : $localize`Turn the toggle on if you want to generate AI based drafts`
      case AutomationHandler.ReviewSendAIReview:
        return status
          ? $localize`Turn the toggle off if you don't want to send AI based review`
          : $localize`Turn the toggle on if you want to send AI based review`
    }
  }
}
